import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import * as LogAPI from '../services/LogServices'
import AuthContext from './AuthContext';

export const ScenarioContext = createContext();

export const ScenarioProvider = ({ children }) => {
    const { anonId } = useContext(AuthContext);
    const [lastUpdate, setLastUpdate] = useState(Date.now());
    const [currentLogInfo, setCurrentLogInfo] = useState(null);
    const [logId, setLogId] = useState(null);
    
    // Tracks "Where am I?" for each scenario: { [scenarioId]: situationId }
    // Rename for clarity? Name implies it's just an array of situations instead of an array of scenarioId->situationId key:value pairs
    const [currentSituations, setCurrentSituations] = useState(() => {
        try {
            const saved = localStorage.getItem('currentSituations');
            return saved ? JSON.parse(saved) : {};
        } catch (error) {
            console.error("Error parsing currentSituations:", error);
            return {};
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem('currentSituations', JSON.stringify(currentSituations));
        } catch (error) {
            console.error("Error saving currentSituations:", error);
        }
    }, [currentSituations]);

    // Rename for clarity? Name implies it's just a situation ID instead of a scenarioId->situationId key:value pair
    const setCurrentSituationId = useCallback((scenarioId, situationId) => {
        setCurrentSituations(prev => ({ ...prev, [scenarioId]: situationId }));
    }, []);


    // Resume an in-progress run on scenario entry. Read-only: pulls the caller's
    // existing log (if any) and rehydrates both the log id and the saved position.
    // Setting logId lets the effect below fetch the full log (incl. decisions), and
    // subsequent submitDecision calls then append to this same log. If there's no
    // active log, any stale local position is cleared so the title page shows and a
    // fresh log is created on Start (keeps "position exists <=> log exists" true).
    // applyPosition=false lets a caller attach the log id without moving the user
    // (e.g. when an explicit deep-link target should win over the saved position).
    const resumeLog = useCallback(async (scenarioId, { applyPosition = true } = {}) => {
        try {
            const { log } = await LogAPI.getActiveLog(scenarioId, anonId);

            if (log) {
                setLogId(log._id);
                if (applyPosition && log.currentSituation) {
                    setCurrentSituationId(scenarioId, log.currentSituation);
                }
            } else if (applyPosition) {
                setCurrentSituationId(scenarioId, null);
            }

            return log || null;
        } catch (error) {
            console.error("Error resuming log:", error);
            return null;
        }
    }, [anonId, setCurrentSituationId]);


    const startOrGetLog = useCallback(async (scenarioId, startSituationId, assignmentId = null) => {
        try {
            
            const logData = await LogAPI.getOrCreateLog(scenarioId, startSituationId, assignmentId, anonId);
            
            setLogId(logData._id);

            setCurrentLogInfo(logData);

            if (logData.currentSituation) {
                setCurrentSituationId(scenarioId, logData.currentSituation);
            }

            return logData;
        } catch (error) {
            console.error("Error initializing log:", error);
        }
    }, [setCurrentSituationId]);


    // payload needs to be this type: { situationId, choiceId, userInput, nextSituationId, correctness, isCompleted, fromToC, assignmentId, type }
    const submitDecision = useCallback(async (payload) => {
        if (!logId) {
            console.warn("Attempted to submit decision without active logId");
            return;
        }

        try {
            const data = await LogAPI.submitDecision(logId, payload);

            setCurrentLogInfo(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    decisions: [
                        ...(prev.decisions || []),
                        {
                            situationId: payload.situationId,
                            choiceId: payload.choiceId,
                            userInput: payload.userInput,
                            type: payload.type,
                            priority: data.priority,
                            timestamp: new Date().toISOString()
                        }
                    ],
                    status: payload.isCompleted ? 'completed' : prev.status,
                    currentSituation: payload.nextSituationId
                };
            });

        } catch (error) {
            console.error("Error submitting decision:", error);
        }
    }, [logId]);


    const triggerUpdate = useCallback(() => {
        setLastUpdate(Date.now());
    }, []);


    useEffect(() => {
        const processInfo = async () => {
            if (!logId) return;

            if (currentLogInfo && currentLogInfo._id === logId) return;
            
            try {
                const data = await LogAPI.getLogDetails(logId);
                setCurrentLogInfo(data);
            } catch (err) {
                console.error("Error fetching log info:", err);
            }
        }
        
        if (logId) processInfo();
    }, [logId]);

    const contextValue = useMemo(() => ({
        lastUpdate,
        triggerUpdate,
        currentSituations,
        setCurrentSituationId,
        startOrGetLog,
        resumeLog,
        logId,
        setLogId,
        submitDecision,
        currentLogInfo,
    }), [
        lastUpdate,
        triggerUpdate,
        currentSituations,
        setCurrentSituationId,
        startOrGetLog,
        resumeLog,
        logId,
        setLogId,
        submitDecision,
        currentLogInfo,
    ]);

    return (
        <ScenarioContext.Provider value={contextValue}>
            {children}
        </ScenarioContext.Provider>
    );
};

export const useScenario = () => {
    const context = useContext(ScenarioContext);
    if (context === undefined) {
        throw new Error('useScenario must be used within a ScenarioProvider');
    }
    return context;
};